import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button } from 'reactstrap';
import { RiFilter3Fill } from 'react-icons/ri';
import { SortingState } from '@tanstack/react-table';
import { toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import StandardLayout from '@/common/layouts/StandardLayout';
import DebouncedInput from '@/components/DebounceInput';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import AssignTicketModal from './components/AssignTicketModal';
import CreateTicketModal from './components/CreateTicketModal';

import {
  useGetTournamentTicketsQuery,
  useDeleteTournamentTicketMutation,
  useAssignTournamentTicketMutation,
  useUnassignTournamentTicketMutation,
  useGetEligibleTargetTournamentsQuery,
  useCreateTicketForUserMutation,
  useRegisterTournamentTicketMutation,
  useUnregisterTournamentTicketMutation,
} from '@/redux/api/tournaments';
import { TournamentTicketStatus } from '@repo/common-types';

import { createColumns } from './components/tournament-tickets.columns';
import { TournamentTicketsFilter } from './components/TournamentTicketsFilter';

interface FilterState {
  status?: TournamentTicketStatus[];
  createdAt?: { from?: Date; to?: Date };
}

const TournamentTicketsPage: React.FC = () => {
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(25);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterState>({});

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | undefined>(undefined);
  const [confirmAction, setConfirmAction] = useState<'delete' | 'unassign' | undefined>(undefined);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignModalData, setAssignModalData] = useState<{
    ticketId: string;
    currentUserId?: string | null;
    isReassign: boolean;
  } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [deleteTicket] = useDeleteTournamentTicketMutation();
  const [assignTicket] = useAssignTournamentTicketMutation();
  const [unassignTicket] = useUnassignTournamentTicketMutation();
  const [createTicket] = useCreateTicketForUserMutation();
  const [registerTicket] = useRegisterTournamentTicketMutation();
  const [unregisterTicket] = useUnregisterTournamentTicketMutation();

  const {
    data: tournamentsData,
    isLoading: isLoadingTournaments,
    isError: isErrorTournaments,
  } = useGetEligibleTargetTournamentsQuery();

  // Build query params
  const queryParams = useMemo(() => {
    const params: any = {
      skip,
      take,
      sortBy: sorting[0]?.id || 'createdAt',
      sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
      filters: {
        ...(filters.status?.length && { status: filters.status }),
        ...(filters.createdAt && { expiresAt: filters.createdAt }),
        ...(searchTerm && { tournamentName: searchTerm }),
      },
    };
    return params;
  }, [skip, take, sorting, searchTerm, filters]);

  const { data, isLoading, error, refetch } = useGetTournamentTicketsQuery(queryParams);

  // Check if filters differ from initial state (default is no filters = show all)
  const hasActiveFilters = useMemo(() => {
    return (filters.status && filters.status.length > 0) || !!filters.createdAt || !!searchTerm;
  }, [filters, searchTerm]);

  const handleSearch = useCallback((value: string | number) => {
    setSearchTerm(value.toString());
    setSkip(0); // Reset to first page on search
  }, []);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
    },
    [sorting],
  );

  const handlePaginationChange = useCallback((newSkip: number, newTake: number) => {
    setSkip(newSkip);
    setTake(newTake);
  }, []);

  const handleDeleteTicket = useCallback((ticketId: string) => {
    setSelectedTicketId(ticketId);
    setConfirmAction('delete');
    setShowConfirmModal(true);
  }, []);

  const handleUnassignTicket = useCallback((ticketId: string) => {
    setSelectedTicketId(ticketId);
    setConfirmAction('unassign');
    setShowConfirmModal(true);
  }, []);

  const handleRegisterTicket = useCallback(
    async (ticketId: string) => {
      try {
        await registerTicket(ticketId).unwrap();
        toast.success('Ticket registration queued! The registration will be processed shortly.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'dark',
          transition: Bounce,
        });
        refetch();
      } catch (error: any) {
        console.error('Error registering ticket:', error);
        const errorMessage = error?.data?.message || 'Failed to register ticket.';
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          theme: 'dark',
          transition: Bounce,
        });
      }
    },
    [registerTicket, refetch],
  );

  const handleUnregisterTicket = useCallback(
    async (ticketId: string) => {
      try {
        await unregisterTicket(ticketId).unwrap();
        toast.success('Ticket unregistration queued! The unregistration will be processed shortly.', {
          position: 'top-right',
          autoClose: 5000,
          theme: 'dark',
          transition: Bounce,
        });
        refetch();
      } catch (error: any) {
        const errorMessage = error?.data?.message || 'Failed to unregister ticket.';
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 3000,
          theme: 'dark',
          transition: Bounce,
        });
      }
    },
    [unregisterTicket, refetch],
  );

  const handleOpenReassignModal = useCallback((ticketId: string, currentUserId: string) => {
    setAssignModalData({
      ticketId,
      currentUserId,
      isReassign: true,
    });
    setShowAssignModal(true);
  }, []);

  const handleOpenAssignModal = useCallback((ticketId: string) => {
    setAssignModalData({
      ticketId,
      currentUserId: null,
      isReassign: false,
    });
    setShowAssignModal(true);
  }, []);

  const handleAssignModalSubmit = useCallback(
    async (userId: string) => {
      if (!assignModalData) return;

      const { ticketId, isReassign } = assignModalData;

      try {
        await assignTicket({ ticketId, userId }).unwrap();

        toast.success(`Ticket ${isReassign ? 'reassigned' : 'assigned'} successfully!`, {
          position: 'top-right',
          autoClose: 3000,
          theme: 'dark',
          transition: Bounce,
        });
        refetch();
      } catch (error: any) {
        console.error(`Error ${isReassign ? 'reassigning' : 'assigning'} ticket:`, error);
        const errorMessage = error?.data?.message || `Failed to ${isReassign ? 'reassign' : 'assign'} ticket.`;
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          theme: 'dark',
          transition: Bounce,
        });
      } finally {
        setShowAssignModal(false);
        setAssignModalData(null);
      }
    },
    [assignModalData, assignTicket, refetch],
  );

  const handleConfirm = useCallback(async () => {
    if (!selectedTicketId) return;

    try {
      if (confirmAction === 'delete') {
        await deleteTicket(selectedTicketId).unwrap();
        toast.success('Ticket deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'dark',
          transition: Bounce,
        });
      } else if (confirmAction === 'unassign') {
        await unassignTicket(selectedTicketId).unwrap();
        toast.success('Ticket unassigned successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'dark',
          transition: Bounce,
        });
      }

      refetch();
    } catch (error: any) {
      console.error(`Error during ${confirmAction}:`, error);
      const errorMessage = error?.data?.message || `Failed to ${confirmAction} ticket.`;
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        theme: 'dark',
        transition: Bounce,
      });
    } finally {
      setShowConfirmModal(false);
      setSelectedTicketId(undefined);
      setConfirmAction(undefined);
    }
  }, [selectedTicketId, confirmAction, deleteTicket, unassignTicket, refetch]);

  const handleCreateTicket = useCallback(
    async (userId: string, tournamentId: string) => {
      try {
        await createTicket({ userId, tournamentId }).unwrap();

        toast.success('Ticket created successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'dark',
          transition: Bounce,
        });
        refetch();
      } catch (error: any) {
        console.error('Error creating ticket:', error);
        const errorMessage = error?.data?.message || 'Failed to create ticket.';
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          theme: 'dark',
          transition: Bounce,
        });
      } finally {
        setShowCreateModal(false);
      }
    },
    [createTicket, refetch],
  );

  const columns = useMemo(
    () =>
      createColumns({
        onDelete: handleDeleteTicket,
        onRegister: handleRegisterTicket,
        onUnregister: handleUnregisterTicket,
        onUnassign: handleUnassignTicket,
        onOpenReassignModal: handleOpenReassignModal,
        onOpenAssignModal: handleOpenAssignModal,
      }),
    [
      handleDeleteTicket,
      handleRegisterTicket,
      handleUnregisterTicket,
      handleUnassignTicket,
      handleOpenReassignModal,
      handleOpenAssignModal,
    ],
  );

  return (
    <StandardLayout pageTitle="Tournament Tickets">
      {error ? (
        <Alert color="danger">Error when fetching tournament tickets data</Alert>
      ) : (
        <>
          <div className="w-100 d-flex justify-content-between align-items-center mb-4 gap-2">
            <div className="d-flex align-items-center justify-content-start gap-2">
              <DebouncedInput
                className="form-control w-auto"
                placeholder="Search by Tournament Name"
                onChange={handleSearch}
              />
              <Button type="button" outline color="dark" onClick={() => setShowFilterModal(true)}>
                <span className="d-flex align-items-center gap-2">
                  <RiFilter3Fill size={20} />
                  Filter
                </span>
              </Button>
              {hasActiveFilters && (
                <Button
                  type="button"
                  outline
                  color="dark"
                  onClick={() => {
                    setFilters({});
                    setSearchTerm('');
                    setSkip(0);
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
            <Button color="primary" onClick={() => setShowCreateModal(true)}>
              <i className="ri-add-line me-1"></i>
              Create Ticket
            </Button>
          </div>

          <DataTable
            data={data?.data || []}
            columns={columns}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            enableSorting={true}
          />

          <Paginator
            take={take}
            skip={skip}
            showing={data?.data?.length || 0}
            totalCount={data?.total || 0}
            setTake={(newTake) => handlePaginationChange(skip, newTake)}
            setSkip={(newSkip) => handlePaginationChange(newSkip, take)}
          />
        </>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <TournamentTicketsFilter
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={(newFilters) => {
            setFilters(newFilters);
            setShowFilterModal(false);
            setSkip(0); // Reset to first page when applying filters
          }}
          currentFilters={filters}
        />
      )}

      {showConfirmModal && (
        <ConfirmModal
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedTicketId(undefined);
            setConfirmAction(undefined);
          }}
          onConfirm={handleConfirm}
          title="Confirm Action"
          message={`Are you sure you want to ${confirmAction} this ticket?`}
        />
      )}

      {showAssignModal && assignModalData && (
        <AssignTicketModal
          onClose={() => {
            setShowAssignModal(false);
            setAssignModalData(null);
          }}
          onSubmit={handleAssignModalSubmit}
          title={assignModalData.isReassign ? 'Reassign Ticket' : 'Assign Ticket'}
          currentUserId={assignModalData.currentUserId}
        />
      )}

      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTicket}
          tournaments={tournamentsData?.data}
          isLoadingTournaments={isLoadingTournaments}
          hasTournamentsError={isErrorTournaments}
        />
      )}
    </StandardLayout>
  );
};

export default TournamentTicketsPage;
